import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Heart, MapPin, ArrowRight, Clock, Question } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import MemoryContributeDialog from "@/components/MemoryContributeDialog";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `${Math.max(1, Math.floor(d / 60))}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function InstitutionMemoryWall({ instName }) {
  const { user } = useAuth();
  const [memories, setMemories] = useState([]);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/public/memories", { params: { institution: instName } }).then((r) => setMemories(r.data)).catch(() => setMemories([]));
  useEffect(() => { if (instName) load(); /* eslint-disable-next-line */ }, [instName]);

  const share = () => { if (!user) return toast.info("Sign in to share your memory"); setOpen(true); };

  const prompts = [
    `What was the ${instName} uniform like in your year?`,
    `Who was your favourite teacher at ${instName}?`,
    `Do you still have a class photo from ${instName}?`,
    `What's your best memory from ${instName}?`,
  ];

  return (
    <section className="mt-10" data-testid="institution-memory-wall">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border/60 pb-3">
        <div>
          <p className="overline text-secondary mb-1 inline-flex items-center gap-1.5"><Clock size={13} weight="duotone" /> Memory Lane</p>
          <h2 className="font-heading text-2xl font-light tracking-tight">Memories from <span className="italic font-medium text-secondary">{instName}</span></h2>
        </div>
        {user ? (
          <Button onClick={share} className="rounded-full gap-1.5" data-testid="wall-share-btn"><Camera size={16} /> Share a memory</Button>
        ) : (
          <Link to="/register"><Button className="rounded-full gap-1.5" data-testid="wall-share-btn"><Camera size={16} /> Share a memory</Button></Link>
        )}
      </div>

      {/* Tailored "Do you remember?" prompts */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="wall-prompts">
        {prompts.map((q) => (
          user ? (
            <button key={q} onClick={share} data-testid="wall-prompt"
              className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:border-secondary/40 hover:text-secondary">
              <Question size={13} weight="duotone" className="text-secondary" /> {q}
            </button>
          ) : (
            <Link key={q} to="/register" data-testid="wall-prompt"
              className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-3 py-1.5 text-xs text-foreground/80 transition-colors hover:border-secondary/40 hover:text-secondary">
              <Question size={13} weight="duotone" className="text-secondary" /> {q}
            </Link>
          )
        ))}
      </div>

      {memories.length === 0 ? (
        <div className="mt-6 grid place-items-center rounded-2xl border border-dashed border-border/70 py-12 text-center">
          <Camera size={34} weight="duotone" className="text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">No memories from {instName} yet. Be the first to add a photo or story.</p>
          <Link to="/memory-lane" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline">Visit Memory Lane <ArrowRight size={13} /></Link>
        </div>
      ) : (
        <>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {memories.slice(0, 6).map((m) => {
              const cover = (m.photo_file_ids || [])[0];
              return (
                <Link key={m.id} to={`/memory-lane/${m.id}`} className="flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm transition-colors hover:border-secondary/40" data-testid={`wall-memory-${m.id}`}>
                  {cover && <img src={fileUrl(cover)} alt={m.title} className="h-40 w-full object-cover" loading="lazy" />}
                  <div className="flex flex-1 flex-col p-4">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="rounded-full bg-accent px-2.5 py-0.5 font-medium text-primary">{m.category}</span>
                      {m.decade && <span className="text-muted-foreground">{m.decade}</span>}
                    </div>
                    <h3 className="mt-1.5 font-heading text-base font-medium leading-tight">{m.title}</h3>
                    {m.memory && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{m.memory}</p>}
                    <div className="mt-auto flex items-center justify-between pt-3 text-xs text-muted-foreground">
                      <span>{m.created_by_name} · {timeAgo(m.created_at)}</span>
                      <span className="inline-flex items-center gap-1"><Heart size={13} /> {(m.likes || []).length}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          {memories.length > 6 && (
            <Link to="/memory-lane" className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-secondary hover:underline">See all {memories.length} memories on Memory Lane <ArrowRight size={13} /></Link>
          )}
        </>
      )}

      <MemoryContributeDialog open={open} onOpenChange={setOpen} defaultInstitution={instName} onCreated={load} />
    </section>
  );
}
